import React from 'react';
import {
  buildOptionsFromData,
  FilteredTable,
  MultiSelectTableFilter,
} from 'works/components/filtered-table';
import { Icon } from '@common/components';
import { StaffingRequestTemplateType } from 'works/graphql/staffing-request-template';
import { TextSearchTableFilter } from 'works/components/filtered-table/text-search-table-filter';
import { formatWorksListTemplates } from './_staffing-request-template-list-helpers';
import { Dropdown, MenuProps } from 'antd';
import { ArchiveStaffingTemplate } from 'works/components/archive-staffing-template/archive-staffing-template';
import {
  FiltersType,
  PaginationDataType,
  SorterType,
  useQuery,
  useNavigate,
} from '@common/hooks';
import {
  OrganizationBusinessType,
  OrganizationUnitType,
} from '@institutions/graphql';
import { ORGANIZATION_UNITS_FLAT_QUERY } from '@institutions/graphql/queries/organization-units-query';
import { WorksOrganizationBusinessesQuery } from 'works/graphql/organization-business';
import { buildDialogUrl } from '@common/services/dialogs-service';

type Props = {
  staffingRequestTemplates: StaffingRequestTemplateType[];
  filters: FiltersType;
  loading: boolean;
  onTableChange: (
    pagination: PaginationDataType & { current: number },
    _: FiltersType,
    sorter: SorterType
  ) => void;
  onFilterChange: (newFilters: FiltersType) => void;
  pagination: {
    total: number;
    filters: FiltersType;
  } & PaginationDataType;
  localStorageFilterKey: string;
};

export const StaffingRequestTemplateList = ({
  staffingRequestTemplates,
  filters,
  loading,
  onTableChange,
  onFilterChange,
  pagination,
  localStorageFilterKey,
}: Props) => {
  const navigate = useNavigate();
  const dataSource = staffingRequestTemplates.map(formatWorksListTemplates);

  const { loading: organizationUnitLoading, data: organizationUnitData } =
    useQuery(ORGANIZATION_UNITS_FLAT_QUERY, {
      fetchPolicy: 'cache-first',
    });

  const {
    loading: organizationBusinessLoading,
    data: organizationBusinessData,
  } = useQuery(WorksOrganizationBusinessesQuery, {
    fetchPolicy: 'cache-first',
  });

  function openCloneDrawer(id: string) {
    navigate(buildStaffingRequestTemplateDrawerUrl(id, 'clone'));
  }

  function openEditDrawer(id: string) {
    navigate(buildStaffingRequestTemplateDrawerUrl(id, 'edit'));
  }

  const dropdownMenuItems = ({
    key,
    canArchive,
  }: {
    key: string;
    canArchive: boolean;
  }): MenuProps['items'] => {
    return [
      {
        key: '1',
        label: <a onClick={() => openCloneDrawer(key)}>Clone</a>,
      },
      {
        key: '2',
        label: <a onClick={() => openEditDrawer(key)}>Edit</a>,
      },
      ...(canArchive
        ? [
            {
              key: '3',
              label: (
                <ArchiveStaffingTemplate staffingRequestTemplateId={key} />
              ),
            },
          ]
        : []),
    ];
  };

  const columns = [
    {
      title: 'Template Name',
      dataIndex: 'name',
      key: 'name',
      width: '25.5%',
      sorter: true,
      filtered: filters.name || false,
      filteredValue: filters.name || null,
      filterDropdown: TextSearchTableFilter,
    },
    {
      title: 'Unit',
      dataIndex: 'organizationUnit',
      key: 'organizationUnit',
      width: '18.5%',
      sorter: true,
      filtered: filters.organizationUnit || false,
      filteredValue: filters.organizationUnit || null,
      filters: buildOptionsFromData(
        organizationUnitData?.organizationUnits || [],
        (organizationUnit: OrganizationUnitType) => ({
          value: organizationUnit.id,
          text: organizationUnit.name,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'External Job ID',
      dataIndex: 'externalId',
      key: 'externalId',
      width: '10%',
      sorter: true,
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      width: '10%',
      sorter: true,
    },
    {
      title: 'Facility',
      dataIndex: 'organizationBusiness',
      key: 'organizationBusiness',
      width: '14.25%',
      sorter: true,
      filtered: filters.organizationBusiness || false,
      filteredValue: filters.organizationBusiness || null,
      filters: buildOptionsFromData(
        organizationBusinessData?.organizationBusinesses,
        (organizationBusiness: OrganizationBusinessType) => ({
          value: organizationBusiness.id,
          text: organizationBusiness.name,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Date Modified',
      dataIndex: 'dateModified',
      key: 'dateModified',
      width: '14.25%',
      sorter: true,
    },
    {
      title: 'Actions',
      dataIndex: 'clone',
      key: 'clone',
      width: '8.25%',
      render: (
        _: unknown,
        record: {
          key: string;
          canArchive: boolean;
        }
      ) => (
        <Dropdown
          className="template-actions-dropdown"
          menu={{
            items: dropdownMenuItems(record),
          }}
          trigger={['click']}
        >
          <a>
            <Icon size="lg" icon="ellipsis" kind="regular" color="elm" />
          </a>
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <section className="staffing-templates-table">
        <FilteredTable
          rowKey={(record: { key: string }) => record.key}
          filters={filters}
          columns={columns}
          dataSource={dataSource}
          loading={
            loading || organizationUnitLoading || organizationBusinessLoading
          }
          onChange={onTableChange}
          onFilterChange={onFilterChange}
          pagination={pagination}
          localStorageFilterKey={localStorageFilterKey}
          scroll={false}
        />
      </section>
      <style jsx>{`
        .staffing-templates-table {
          width: 100%;
        }
        :global(.staffing-templates-table .ant-table-tbody > tr > td) {
          padding: 20px 4px 20px 8px;
        }
        :global(.staffing-templates-action) {
          margin: 0 8px 0 0;
          cursor: pointer;
        }
        :global(.staffing-templates-item) {
          margin-bottom: 4px;
        }
      `}</style>
    </>
  );
};

function buildStaffingRequestTemplateDrawerUrl(id: string, action: string) {
  return buildDialogUrl('staffing-request-template-drawer', {
    props: {
      action,
      templateId: id,
    },
  });
}
